import { alpha, createTheme } from '@mui/material/styles';
import accessibleModalManager from './components/AccessibleModalManager';

// Edit shared colors/borders here; both MUI and SCSS consume these tokens.
export const SHARED_DESIGN = {
  radius: 12,
};
export const panelSx = {
  border: 1, borderColor: 'divider', borderRadius: 1,
  bgcolor: 'background.paper', color: 'text.primary', p: { xs: 2, sm: 3 }, mb: 3,
};
export const feedbackRecordSx = {
  my: 1,
  '& .MuiCardContent-root': { p: 1.5, '&:last-child': { pb: 1.5 } },
};
const ACTION_TEXT = { daylight: '#ffffff', evening: '#fff8eb', midnight: '#f7fbff', dawn: '#fff7fb' };
export const getThemeVariables = (name) => {
  const colors = THEME_COLORS[name] || THEME_COLORS.daylight;
  return { bg: colors.bg, 'bg-soft': colors.soft, surface: colors.surface,
    'surface-raised': colors.raised, accent: colors.primary, 'accent-warm': colors.secondary,
    text: colors.text, 'text-muted': colors.muted, border: colors.border,
    danger: colors.danger, 'danger-soft': colors.dangerSoft, 'danger-text': colors.dangerText,
    success: colors.success, 'success-soft': colors.successSoft, 'success-text': colors.successText,
    warning: colors.warning, 'warning-soft': colors.warningSoft, 'warning-text': colors.warningText,
    info: colors.info, 'info-soft': colors.infoSoft, 'info-text': colors.infoText,
    shadow: colors.shadow, overlay: colors.overlay, subtle: colors.subtle,
    toolbar: colors.toolbar, announcement: colors.announcement, 'announcement-text': colors.announcementText,
    'announcement-border': colors.announcementBorder, recovery: colors.recovery, 'recovery-text': colors.recoveryText,
    'action-create': `linear-gradient(145deg, ${colors.actionCreate[0]}, ${colors.actionCreate[1]})`,
    'action-join': `linear-gradient(145deg, ${colors.actionJoin[0]}, ${colors.actionJoin[1]})`,
    'action-random': `linear-gradient(145deg, ${colors.actionRandom[0]}, ${colors.actionRandom[1]})`,
    'action-text': ACTION_TEXT[name] || '#ffffff',
    'choice-one': `linear-gradient(145deg, ${colors.choiceOne[0]}, ${colors.choiceOne[1]})`,
    'choice-two': `linear-gradient(145deg, ${colors.choiceTwo[0]}, ${colors.choiceTwo[1]})`,
    radius: `${SHARED_DESIGN.radius}px`,
    'accent-text': colors.accentText, 'accent-border': alpha(colors.primary, .5),
    'warm-glow': alpha(colors.secondary, .1),
    glow: colors.glow, desk: colors.desk, felt: colors.felt,
    'surface-gradient': `linear-gradient(145deg, ${colors.raised}, ${colors.surface})` };
};

// Shared visual language for every MUI surface. Component-specific layout
// remains in each page so this theme cannot alter application behavior.
export const THEME_COLORS = {
  daylight: {
    mode: 'light', primary: '#0369a1', secondary: '#a34408', bg: '#f8fcff', soft: '#e5f3fc', surface: '#ffffff', raised: '#edf7fd', text: '#10243d', muted: '#4b6178', border: 'rgba(12,74,110,.28)', accentText: '#ffffff', glow: 'rgba(2,132,199,.17)', desk: 'rgba(186,230,253,.58)', felt: 'linear-gradient(145deg, #23a37b, #0b7255 68%)',
    success: '#137a4b', successSoft: '#dff6e9', successText: '#0a5231', danger: '#b42343', dangerSoft: '#fde5eb', dangerText: '#7f1730', warning: '#9a5704', warningSoft: '#fff0cc', warningText: '#673800', info: '#075f96', infoSoft: '#dff2ff', infoText: '#06476f', shadow: 'rgba(15,50,74,.22)', overlay: 'rgba(240,249,255,.82)', subtle: 'rgba(3,105,161,.09)', toolbar: '#eef8fe', announcement: '#fff1c7', announcementText: '#633600', announcementBorder: '#b96a06', recovery: '#fff4d8', recoveryText: '#5c3500', actionCreate: ['#11845f', '#075f49'], actionJoin: ['#0878bd', '#07548a'], actionRandom: ['#7851b8', '#53338d'], choiceOne: ['#0f8b6a', '#08725a'], choiceTwo: ['#3f63c9', '#294797'],
  },
  evening: {
    mode: 'dark', primary: '#ffc857', secondary: '#ff7f6e', bg: '#27120d', soft: '#452116', surface: '#382019', raised: '#5c3023', text: '#fff8eb', muted: '#efd0b8', border: 'rgba(255,210,158,.36)', accentText: '#382000', glow: 'rgba(255,132,61,.24)', desk: 'rgba(122,51,25,.52)', felt: 'linear-gradient(145deg, #9c5b32, #5d311e 68%)',
    success: '#75d99a', successSoft: '#173d29', successText: '#baf4cf', danger: '#ff8a8f', dangerSoft: '#522126', dangerText: '#ffd1d3', warning: '#ffc857', warningSoft: '#513813', warningText: '#ffe5aa', info: '#70c7e8', infoSoft: '#173b48', infoText: '#c6efff', shadow: 'rgba(18,5,2,.62)', overlay: 'rgba(31,10,5,.82)', subtle: 'rgba(255,200,87,.13)', toolbar: '#32170f', announcement: '#6a2b16', announcementText: '#fff1cf', announcementBorder: '#ffad45', recovery: '#5a2d17', recoveryText: '#fff0cd', actionCreate: ['#a55828', '#723419'], actionJoin: ['#9b3f39', '#672a2c'], actionRandom: ['#74406f', '#4d294d'], choiceOne: ['#a85c2c', '#6f351e'], choiceTwo: ['#9d3f45', '#632b36'],
  },
  midnight: {
    mode: 'dark', primary: '#43c7ff', secondary: '#a990ff', bg: '#010207', soft: '#060a12', surface: '#0a0f19', raised: '#121c2c', text: '#f7fbff', muted: '#b9c8dc', border: 'rgba(142,170,204,.32)', accentText: '#02141d', glow: 'rgba(44,185,255,.15)', desk: 'rgba(3,11,27,.78)', felt: 'linear-gradient(145deg, #123650, #051724 68%)',
    success: '#4ade80', successSoft: '#0d321f', successText: '#bbf7d0', danger: '#fb7185', dangerSoft: '#3f1420', dangerText: '#fecdd3', warning: '#facc15', warningSoft: '#352c08', warningText: '#fef08a', info: '#38bdf8', infoSoft: '#0b2d40', infoText: '#bae6fd', shadow: 'rgba(0,0,0,.75)', overlay: 'rgba(0,2,8,.86)', subtle: 'rgba(67,199,255,.1)', toolbar: '#03060d', announcement: '#352705', announcementText: '#fff3b0', announcementBorder: '#d9a900', recovery: '#302607', recoveryText: '#fff2ad', actionCreate: ['#075e54', '#043b37'], actionJoin: ['#123f8f', '#0a275e'], actionRandom: ['#4c3188', '#2c205a'], choiceOne: ['#075f57', '#073d42'], choiceTwo: ['#3730a3', '#1e235f'],
  },
  dawn: {
    mode: 'light', primary: '#9d174d', secondary: '#b9380a', bg: '#fff7fb', soft: '#fbe5f1', surface: '#fffafd', raised: '#f7e5ef', text: '#351529', muted: '#704c62', border: 'rgba(125,24,65,.28)', accentText: '#ffffff', glow: 'rgba(219,39,119,.18)', desk: 'rgba(251,207,232,.6)', felt: 'linear-gradient(145deg, #946983, #68445f 68%)',
    success: '#317552', successSoft: '#e2f2e8', successText: '#1f5238', danger: '#b31f51', dangerSoft: '#fde4ee', dangerText: '#80133a', warning: '#975009', warningSoft: '#fff0d7', warningText: '#633300', info: '#5960a8', infoSoft: '#ebeaff', infoText: '#3b407c', shadow: 'rgba(83,31,58,.24)', overlay: 'rgba(255,247,251,.84)', subtle: 'rgba(157,23,77,.09)', toolbar: '#fcebf4', announcement: '#ffe8cc', announcementText: '#713600', announcementBorder: '#b55a0a', recovery: '#fbe7d6', recoveryText: '#683300', actionCreate: ['#8e5274', '#673850'], actionJoin: ['#7860aa', '#51417e'], actionRandom: ['#b14f72', '#7d3654'], choiceOne: ['#a34d75', '#713651'], choiceTwo: ['#7662a9', '#50427d'],
  },
};

export const createAppTheme = (name = 'daylight') => {
  const colors = THEME_COLORS[name] || THEME_COLORS.daylight;
  return createTheme({
  palette: {
    mode: colors.mode,
    primary: { main: colors.primary, contrastText: colors.accentText },
    secondary: { main: colors.secondary, contrastText: colors.accentText },
    success: { main: colors.success },
    error: { main: colors.danger },
    warning: { main: colors.warning },
    info: { main: colors.info },
    background: { default: colors.bg, paper: colors.surface },
    text: { primary: colors.text, secondary: colors.muted },
    divider: colors.border,
  },
  typography: {
    fontFamily: "'Noto Sans Myanmar', 'Noto Sans JP', 'Segoe UI', Roboto, sans-serif",
    body1: { lineHeight: 1.6 },
    body2: { lineHeight: 1.6 },
    button: { fontWeight: 700, lineHeight: 1.5 },
    h5: { fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.45 },
    h6: { fontWeight: 750, lineHeight: 1.45 },
  },
  shape: { borderRadius: SHARED_DESIGN.radius },
  components: {
    MuiModal: {
      defaultProps: { manager: accessibleModalManager, disableRestoreFocus: true },
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 999, textTransform: 'none', minHeight: 40, whiteSpace: 'nowrap' },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: `1px solid ${colors.border}`,
          backgroundImage: `linear-gradient(145deg, ${colors.raised}, ${colors.surface})`,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          border: `1px solid ${colors.border}`,
          backgroundImage: `linear-gradient(145deg, ${colors.raised}, ${colors.surface})`,
          boxShadow: `0 24px 70px ${colors.shadow}`,
        },
      },
    },
    MuiTextField: {
      defaultProps: { color: 'primary' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { overflow: 'visible' },
        input: { lineHeight: 1.55 },
        notchedOutline: { borderColor: colors.border, overflow: 'visible' },
      },
    },
    MuiInputLabel: {
      styleOverrides: { root: { color: colors.muted, lineHeight: 1.5, overflow: 'visible' } },
    },
    MuiFormHelperText: {
      styleOverrides: { root: { lineHeight: 1.55, overflowWrap: 'anywhere' } },
    },
    MuiMenu: {
      styleOverrides: { paper: { border: `1px solid ${colors.border}` } },
    },
  },
  });
};

export default createAppTheme();
